import { useState } from "react";
import { Folder as FolderIcon, FolderOpen, ArrowRight } from "@phosphor-icons/react";
import FolderSummaryChart from "./FolderSummaryChart";
import ProjectCardMenu from "./ProjectCardMenu";

// Dashboard card that represents a Folder (summary view at the root level).
// - Double-click → drill into the folder (open=true).
// - Single click on the header area opens as well (more discoverable on trackpads).
// - Accepts drops of project cards (HTML5 DnD) and fires onDropProject(projectId).
//
// Props:
//   folder: { id, name, color, project_count, min_start, max_end }
//   nestedProjects: [{ id, name, start_date, go_live_date }] — used to render the summary chart
//   onOpen, onDelete, onRename, onDropProject, onReportIssue
export default function FolderCard({
  folder,
  nestedProjects = [],
  onOpen,
  onDelete,
  onRename,
  onDropProject,
  onReportIssue,
}) {
  const [dragging, setDragging] = useState(false);

  return (
    <div
      className={`relative rounded-md bg-white border p-5 transition-colors group ${
        dragging ? "border-ink ring-2 ring-neon bg-mint/40" : "border-slate-300 hover:border-slate-950"
      }`}
      data-testid={`folder-card-${folder.id}`}
      onDragOver={(e) => {
        // Accept project cards only
        if (!e.dataTransfer.types.includes("application/x-progantt-project")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!dragging) setDragging(true);
      }}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes("application/x-progantt-project")) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        setDragging(false);
        const projectId = e.dataTransfer.getData("application/x-progantt-project");
        if (projectId) {
          e.preventDefault();
          onDropProject?.(projectId);
        }
      }}
      onDoubleClick={onOpen}
    >
      {/* Kebab */}
      <div className="absolute top-3 right-3 z-10">
        <ProjectCardMenu
          testidSuffix={`folder-${folder.id}`}
          canDelete
          canDuplicate={false}
          onReportIssue={onReportIssue}
          onDelete={onDelete}
        />
      </div>

      {/* Header */}
      <button
        type="button"
        onClick={onOpen}
        className="text-left w-full pr-8"
        data-testid={`open-folder-${folder.id}`}
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div
            className="h-9 w-9 rounded-md flex items-center justify-center text-white shrink-0"
            style={{ background: folder.color || "#241623" }}
          >
            {dragging ? <FolderOpen size={16} weight="fill" /> : <FolderIcon size={16} weight="fill" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-bold tracking-tight text-slate-950 truncate">
                {folder.name}
              </h3>
              {onRename && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRename(); }}
                  className="opacity-0 group-hover:opacity-100 text-[10px] font-mono-ibm uppercase tracking-widest text-slate-400 hover:text-slate-950"
                  data-testid={`rename-folder-${folder.id}`}
                >
                  Rename
                </button>
              )}
            </div>
            <div className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500 mt-0.5">
              Folder · {folder.project_count || nestedProjects.length} project{(folder.project_count || nestedProjects.length) === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      </button>

      <FolderSummaryChart
        projects={nestedProjects}
        minStart={folder.min_start}
        maxEnd={folder.max_end}
      />

      <button
        type="button"
        onClick={onOpen}
        className="mt-3 flex items-center gap-1 text-[11px] font-mono-ibm uppercase tracking-widest text-slate-500 hover:text-slate-950"
        data-testid={`open-folder-cta-${folder.id}`}
      >
        Open folder <ArrowRight size={11} weight="bold" />
      </button>
    </div>
  );
}
